import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Avatar, 
  Chip,
  Button, 
  Rating, 
  LinearProgress,
  useTheme,
  styled
} from '@mui/material';
import { 
  CheckCircle as AvailableIcon, 
  DoNotDisturb as BusyIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { Executive } from '../../types/executive.ts';
import { getAvatarComponent } from '../../utils/getAvatarComponent.tsx';
//import HolographicBackground from '../common/HolographicBackground.tsx';

interface ExecutiveCardProps {
  executive: Executive;
  onClick?: () => void;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    border: '1px solid rgba(0, 255, 255, 0.3)',
    boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
  },
}));

const SelectButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #003366 30%, #8A2BE2 90%)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #00CC99 30%, #00ffff 90%)',
    boxShadow: '0 0 15px rgba(0, 255, 255, 0.5)',
  }
}));

const ExecutiveCard: React.FC<ExecutiveCardProps> = ({ executive, onClick }) => {
  const theme = useTheme();
  const { 
    name, 
    title, 
    avatarType, 
    specialty = 'General Consulting', 
    status = 'available',
    rating = 4.5,
    completedTasks = 24
  } = executive;

  const avatarComponent = getAvatarComponent(avatarType || 'person', theme.palette.primary.main, 40);
  
  const statusIcon = status === 'available' ? 
    <AvailableIcon sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} /> : 
    <BusyIcon sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />;
  
  const statusText = status === 'available' ? 'Available' : 'Busy';
  
  return (
    <StyledCard onClick={onClick}>
      <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              width: 56, 
              height: 56,
              mr: 2
            }}
          >
            {typeof avatarComponent === 'string' ? (
              <img src={avatarComponent} alt={name} style={{ width: '100%', height: '100%' }} />
            ) : (
              avatarComponent
            )}
          </Avatar>
          
          <Box>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {statusIcon}
              <Typography variant="caption" color={status === 'available' ? 'success.main' : 'error.main'}>
                {statusText}
              </Typography>
            </Box>
            <SelectButton variant="contained" fullWidth>
              Select
            </SelectButton>
          </Box>
        </Box>
        
        <Chip 
          label={specialty} 
          size="small" 
          sx={{ 
            alignSelf: 'flex-start', 
            mb: 2,
            bgcolor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
          }} 
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Rating 
            value={rating} 
            precision={0.5} 
            readOnly 
            size="small"
            emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
          />
          <Typography variant="body2" sx={{ ml: 1 }}>
            {rating.toFixed(1)}
          </Typography>
        </Box>
        
        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Completed Tasks
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {completedTasks}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(completedTasks, 100)} 
            sx={{ 
              height: 6, 
              borderRadius: 1,
              bgcolor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                bgcolor: theme.palette.success.main,
              }
            }} 
          />
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default ExecutiveCard; 